import { useState, useEffect, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, FileText, Image as ImageIcon } from "lucide-react";
import { api } from "@/lib/apiClient";
import { toast } from "sonner";
import { PN_TYPE_LABELS } from "@/types/design";
import { PnPhotoUpload } from "@/components/Design/PnPhotoUpload";

interface Props {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  designProjectId: string;
  defaultClientCode?: string;
  defaultProjectCode?: string;
  pn?: any | null;          // si viene -> modo edición
  onSaved?: () => void;
}

const padN = (v: string, n: number) => {
  const digits = (v || "").replace(/\D/g, "");
  if (!digits) return "".padStart(n, "0");
  return digits.padStart(n, "0").slice(-n);
};

export function PartNumberDialog({ open, onOpenChange, designProjectId, defaultClientCode, defaultProjectCode, pn, onSaved }: Props) {
  const isEdit = !!pn;
  const locked = isEdit && !!pn?.validated; // PN validado: no recalcular código
  const [loading, setLoading] = useState(false);
  const [f, setF] = useState({
    pn_type: "1", subassembly: "", num_z: "", num_w: "", version: "1",
    client_part_pn: "", name: "", photo_url: "", notes: "",
  });

  // cliente/proyecto derivados del proyecto (no se reintroducen)
  const cliente = (defaultClientCode || "").toUpperCase();
  const proyecto = (defaultProjectCode || "").toUpperCase();

  useEffect(() => {
    if (!open) return;
    if (isEdit) {
      setF({
        pn_type: String(pn.pn_type || "1"),
        subassembly: pn.subassembly || "", num_z: pn.num_z || "", num_w: pn.num_w || "", version: pn.version || "1",
        client_part_pn: pn.client_part_pn || "", name: pn.name || "", photo_url: pn.photo_url || "", notes: pn.notes || "",
      });
    } else {
      setF({ pn_type: "1", subassembly: "", num_z: "", num_w: "", version: "1", client_part_pn: "", name: "", photo_url: "", notes: "" });
    }
  }, [open, pn, isEdit]);

  const buildCode = (type: string) =>
    `${cliente}${proyecto}-${type}-${padN(f.subassembly, 4)}-${padN(f.num_z, 4)}-${padN(f.num_w, 4)}-${padN(f.version, 3)}`;

  const pnCode = useMemo(() => (locked ? pn.pn_code : buildCode(f.pn_type)), [f, locked, cliente, proyecto]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!cliente || !proyecto) { toast.error("El proyecto no tiene código de cliente/proyecto. Asígnalo en el proyecto vinculado."); return; }
    setLoading(true);
    try {
      if (isEdit) {
        const payload: any = {
          client_part_pn: f.client_part_pn || null,
          name: f.name || null,
          photo_url: f.photo_url || null,
          notes: f.notes || null,
        };
        if (!locked) {
          payload.pn_code = pnCode;
          payload.pn_type = parseInt(f.pn_type);
          payload.subassembly = padN(f.subassembly, 4);
          payload.num_z = padN(f.num_z, 4);
          payload.num_w = padN(f.num_w, 4);
          payload.version = padN(f.version, 3);
        }
        const { error } = await api.from("design_part_numbers").update(payload).eq("id", pn.id);
        if (error) throw error;
        toast.success("PN actualizado");
      } else {
        const { error } = await api.from("design_part_numbers").insert([{
          design_project_id: designProjectId,
          pn_code: pnCode,
          pn_type: parseInt(f.pn_type),
          client_part_pn: f.client_part_pn || null,
          subassembly: padN(f.subassembly, 4),
          num_z: padN(f.num_z, 4),
          num_w: padN(f.num_w, 4),
          version: padN(f.version, 3),
          name: f.name || null,
          photo_url: f.photo_url || null,
          notes: f.notes || null,
        }]);
        if (error) throw error;

        // Master (tipo 2) -> crear PN de Molde análogo (tipo 3)
        if (parseInt(f.pn_type) === 2) {
          const moldeCode = buildCode("3");
          const { error: me } = await api.from("design_part_numbers").insert([{
            design_project_id: designProjectId,
            pn_code: moldeCode,
            pn_type: 3,
            client_part_pn: f.client_part_pn || null,
            subassembly: padN(f.subassembly, 4),
            num_z: padN(f.num_z, 4),
            num_w: padN(f.num_w, 4),
            version: padN(f.version, 3),
            name: f.name ? `Molde de ${f.name}` : "Molde",
            notes: `Auto-generado desde Master ${pnCode}`,
          }]);
          if (me && !/duplicate|uq_design_pn_code/.test(me.message || "")) throw me;
          toast.success(`PN Master generado: ${pnCode} · Molde análogo: ${moldeCode}`);
        } else {
          toast.success("PN generado: " + pnCode);
        }
      }
      onOpenChange(false);
      onSaved?.();
    } catch (err: any) {
      if ((err.message || "").includes("uq_design_pn_code") || (err.message || "").includes("duplicate"))
        toast.error("Ese PN ya existe. Cambia algún número o versión.");
      else toast.error(err.message || "No se pudo guardar el PN");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Editar Part Number (PN)" : "Generar Part Number (PN)"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          {/* Vista previa del PN */}
          <div className="rounded-lg bg-primary/5 border border-primary/20 p-3 text-center">
            <p className="text-xs text-muted-foreground mb-1">PN {locked ? "(validado, no editable)" : "resultante"}</p>
            <p className="text-lg font-mono font-bold tracking-wide">{pnCode}</p>
            <p className="text-[11px] text-muted-foreground mt-1">Cliente <b>{cliente || "—"}</b> · Proyecto <b>{proyecto || "—"}</b> · Y: 1 Pieza · 2 Master · 3 Molde · 4 Utillaje</p>
            {!isEdit && <p className="text-[11px] text-amber-600 mt-1">Al generar un Master se crea automáticamente su Molde análogo.</p>}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Tipo (Y)</Label>
              <Select value={f.pn_type} onValueChange={(v) => setF({ ...f, pn_type: v })} disabled={locked}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(PN_TYPE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{k} · {v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Subensamblaje (XXXX)</Label>
              <Input value={f.subassembly} onChange={(e) => setF({ ...f, subassembly: e.target.value })} placeholder="0003" disabled={locked} />
            </div>
            <div>
              <Label>Nº (ZZZZ)</Label>
              <Input value={f.num_z} onChange={(e) => setF({ ...f, num_z: e.target.value })} placeholder="0001" disabled={locked} />
            </div>
            <div>
              <Label>Nº (WWWW)</Label>
              <Input value={f.num_w} onChange={(e) => setF({ ...f, num_w: e.target.value })} placeholder="0003" disabled={locked} />
            </div>
            <div>
              <Label>Versión (AAA)</Label>
              <Input value={f.version} onChange={(e) => setF({ ...f, version: e.target.value })} placeholder="001" disabled={locked} />
            </div>
            <div>
              <Label>Nombre</Label>
              <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Ej: Molde culata ALA" />
            </div>
          </div>

          <div>
            <Label>PN de pieza de cliente (asociado)</Label>
            <Input value={f.client_part_pn} onChange={(e) => setF({ ...f, client_part_pn: e.target.value })} placeholder="Referencia de pieza del cliente" />
          </div>
          <div>
            <Label>Foto del PN (imagen o PDF, para la BOM)</Label>
            <div className="flex items-center gap-2 mt-1">
              {f.photo_url ? (
                /\.pdf($|\?)/i.test(f.photo_url)
                  ? <a href={f.photo_url} target="_blank" rel="noreferrer" className="w-12 h-12 rounded border flex items-center justify-center text-primary bg-primary/5"><FileText className="w-5 h-5" /></a>
                  : <img src={f.photo_url} alt="" className="w-12 h-12 rounded object-cover border" />
              ) : (
                <div className="w-12 h-12 rounded border flex items-center justify-center text-muted-foreground"><ImageIcon className="w-5 h-5" /></div>
              )}
              <PnPhotoUpload size="sm" onUploaded={(url) => setF((p) => ({ ...p, photo_url: url }))} />
              {f.photo_url && <Button type="button" variant="ghost" size="sm" onClick={() => setF((p) => ({ ...p, photo_url: "" }))}>Quitar</Button>}
            </div>
            <Input value={f.photo_url} onChange={(e) => setF({ ...f, photo_url: e.target.value })} placeholder="o pega una URL... (opcional)" className="mt-2" />
          </div>
          <div>
            <Label>Notas</Label>
            <Textarea value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} rows={2} />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button type="submit" disabled={loading}>{loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}{isEdit ? "Guardar" : "Generar PN"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
